/** The expanded detail for one preflight item: step nav + config field editor +
 *  per-action custom UI + dashboard links + rendered steps + error. Shared by the
 *  Preflight tab (inline Collapse) and the system-wide Items drawer. Renders a
 *  fragment — callers own the surrounding padding. */
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Alert, AlertTitle, Box, Button, ButtonGroup, Link, Stack, Tooltip, Typography } from '@mui/material';

import type { PreflightActionRun, PreflightBgTaskRef, PreflightItem, PreflightStatus } from '@/api/preflight';
import { HtmlContent } from '@/components/RichText/HtmlContent';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { bgtaskActions } from '../tenantLinks';

import { ActionCustomUI } from './ActionCustomUI';
import { PreflightFieldEditor } from './PreflightFieldEditor';
import { RestechKeyProbes } from './RestechKeyProbe';

/** The background task a data-sync item spawned. It has been in `context` since
 *  the async actions landed and was never rendered — "did the sync actually
 *  run?" meant guessing at /bgtasks. */
function bgtaskOf(item: PreflightItem): PreflightBgTaskRef | null {
  const raw = item.context?.bgtask;
  if (!raw || typeof raw !== 'object') return null;
  const ref = raw as Partial<PreflightBgTaskRef>;
  return typeof ref.id === 'string' && ref.id
    ? { id: ref.id, task_type: String(ref.task_type ?? 'task'), spawned_ts: ref.spawned_ts }
    : null;
}

interface Props {
  item: PreflightItem;
  topId: number;
  toCode: string;
  canManage: boolean;
  pending: boolean;
  onConfigure: (values: Record<string, unknown>) => void | Promise<unknown>;
  onStepChange: (step: number, status?: PreflightStatus) => void;
  onChanged: () => void;
  /** The last save of this item's fields (its configure run), shown until dismissed. */
  lastSave?: PreflightActionRun | null;
  onDismissLastSave?: () => void;
}

export function PreflightItemDetailContent({
  item,
  topId,
  toCode,
  canManage,
  pending,
  onConfigure,
  onStepChange,
  onChanged,
  lastSave,
  onDismissLastSave,
}: Props) {
  const totalSteps = item.rendered_steps.length;
  const isMultistep = item.task_type === 'multistep' && totalSteps > 0;
  const bgtask = bgtaskOf(item);

  const forwardStatus = (newStep: number): PreflightStatus | undefined => {
    if (newStep >= totalSteps) return 'completed';
    if (item.status === 'pending') return 'started';
    return undefined;
  };
  const backwardStatus = (newStep: number): PreflightStatus | undefined => {
    if (item.step > 0 && newStep === 0) return 'pending';
    if (item.status === 'completed') return 'started';
    return undefined;
  };

  return (
    <>
      {isMultistep && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Step {item.step} of {totalSteps}
          </Typography>
          {canManage && (
            <>
              <ButtonGroup size="small" variant="outlined" disabled={pending}>
                <Button
                  startIcon={<NavigateBeforeIcon />}
                  disabled={item.step <= 0}
                  onClick={() => onStepChange(item.step - 1, backwardStatus(item.step - 1))}
                >
                  Previous
                </Button>
                <Button
                  endIcon={<NavigateNextIcon />}
                  disabled={item.step >= totalSteps}
                  onClick={() => onStepChange(item.step + 1, forwardStatus(item.step + 1))}
                >
                  Next
                </Button>
              </ButtonGroup>
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<RestartAltIcon />}
                disabled={pending || item.step === 0}
                onClick={() => onStepChange(0, backwardStatus(0))}
              >
                Reset
              </Button>
            </>
          )}
        </Box>
      )}
      {/* Above the fields: a key another tenant holds must be read before anything else. */}
      <RestechKeyProbes item={item} canManage={canManage} pending={pending} onConfigure={onConfigure} />
      {item.config_fields.length > 0 && (
        <PreflightFieldEditor
          fields={item.config_fields}
          topId={topId}
          itemId={item.id}
          canManage={canManage}
          saving={pending}
          missingInputs={item.status === 'completed' ? [] : item.missing_inputs}
          onSave={onConfigure}
        />
      )}
      {lastSave && (lastSave.summary || lastSave.error_message) && (
        <Alert
          severity={lastSave.status === 'error' ? 'error' : 'info'}
          onClose={onDismissLastSave}
          sx={{ mb: 1.5 }}
        >
          <AlertTitle>Last save</AlertTitle>
          {lastSave.status === 'error' ? lastSave.error_message || lastSave.summary : lastSave.summary}
        </Alert>
      )}
      <ActionCustomUI item={item} topId={topId} toCode={toCode} canManage={canManage} onChanged={onChanged} />
      {bgtask && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
          Background task:{' '}
          <ValueActions actions={bgtaskActions(bgtask.id)}>{bgtask.task_type}</ValueActions>
          {bgtask.spawned_ts && ` · started ${new Date(bgtask.spawned_ts).toLocaleString()}`}
        </Typography>
      )}
      {item.resources.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Dashboards:
          </Typography>
          {/* `help` says what to do once you get there. It was fetched and dropped,
              leaving every third-party link a bare label. */}
          {item.resources.map((r) => (
            <Tooltip key={r.url} title={r.help || ''}>
              <Link
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  ...(r.help ? { textDecorationStyle: 'dotted' } : {}),
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
                {r.label}
              </Link>
            </Tooltip>
          ))}
        </Box>
      )}
      {/* No "No steps for this item." — most actions have a single step, so that
          negative empty state was on the majority of items, saying nothing. */}
      {totalSteps > 0 && (
        <Stack spacing={0.75}>
          {item.rendered_steps.map((step, idx) => (
            <Box key={idx} sx={{ opacity: idx + 1 === item.step ? 1 : 0.6 }}>
              <HtmlContent html={step.title} sx={{ fontWeight: idx + 1 === item.step ? 700 : 600 }} />
              {step.description && <HtmlContent html={step.description} sx={{ color: 'text.secondary' }} />}
            </Box>
          ))}
        </Stack>
      )}
      {item.status === 'error' && item.error_message && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {item.error_message}
        </Alert>
      )}
    </>
  );
}
